import React, { memo, useState, useCallback, useEffect } from 'react';
import { createUseStyles, Select } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { getEntityValue } from 'lib/services/valueLists';

import styles from './styles';

const useStyles = createUseStyles(styles, 'SelectCell');

const hasSelectObjectWithId = (raw) =>
    raw != null &&
    typeof raw === 'object' &&
    Object.prototype.hasOwnProperty.call(raw, 'value') &&
    raw.value !== undefined &&
    raw.value !== null;

const SelectCell = memo((params) => {
    const { t } = useWeb5Context();
    const [value, setValue] = useState(() =>
        hasSelectObjectWithId(params.value) ? params.value : undefined,
    );
    const [error, setError] = useState('');
    const classes = useStyles();

    const field = params.colDef.field;
    const { onChange, isMulti, list, ...inputParams } = params.inputParams;

    const handleOnChange = useCallback(
        (newValue) => {
            const id = params.data.id || params.data.Id || params.data.ID || '';
            onChange({ rowIndex: params.node.rowIndex, id, field, value: newValue });
            setValue(newValue);
        },
        [onChange, field, params],
    );

    useEffect(() => {
        const rowIndex = params.node.rowIndex;
        if (!inputParams?.errors?.rows?.[rowIndex]?.error) {
            setError('');
        } else {
            const errorText = inputParams?.errors?.rows?.[rowIndex]?.error;
            setError(errorText);
        }
    }, [inputParams.errors, params]);

    useEffect(() => {
        const raw = params?.value;
        // Resolve primitive id (including 0) to { value, label }; do not treat 0 as "empty".
        if (
            hasSelectObjectWithId(raw) ||
            raw === null ||
            raw === undefined ||
            raw === '' ||
            value
        ) {
            return;
        }

        // when only a value is provided from backend
        getEntityValue(list, isMulti ? raw : [raw]).then((result) => {
            if (!result?.data) return;
            const finalValue = isMulti
                ? params?.value?.map((v) => {
                      const row = result.data[v] ?? result.data[String(v)];
                      return {
                          label: row?.description,
                          value: String(row?.id),
                      };
                  })
                : (() => {
                      const row = result.data[raw] ?? result.data[String(raw)];
                      if (row == null) return null;
                      return {
                          label: row.description,
                          value: String(row.id),
                      };
                  })();
            if (finalValue != null) {
                setValue(finalValue);
            }
        });
    }, [isMulti, list, params?.value, value]);

    if (!params.inputParams) return null;

    const placeholder = inputParams.isReadOnly
        ? t('label_advanced_filter_option_empty')
        : t('label_selectone');

    return (
        <div className={['w5-cell', classes.editableCell].join(' ')}>
            <Select
                value={value}
                onChange={handleOnChange}
                list={list}
                isMulti={isMulti}
                {...inputParams}
                placeholder={placeholder}
                error={error}
                isFullWidth
            />
        </div>
    );
});

export default SelectCell;
